import { useState, useEffect } from 'react';
import {
  X,
  Building2,
  Users,
  Plus,
  Trash2,
  Save,
  Phone,
  Mail,
  MapPin,
  Star,
  DollarSign,
  Calendar,
  Info,
  ChevronRight,
  ChevronDown,
  Edit,
  CreditCard,
  Landmark,
  Clock
} from 'lucide-react';
import api from '../api/config';
import './BusinessPartnerModal.css';

const BusinessPartnerModal = ({ isOpen, onClose, partner, type, openContactsDirectly = false }) => {
  const [formData, setFormData] = useState({
    // Información básica
    legalName: '',
    tradeName: '',
    taxId: '',
    isClient: type === 'client',
    isSupplier: type === 'supplier',

    // Información de contacto
    address: '',
    city: '',
    country: 'Chile',
    phone: '',
    email: '',
    website: '',

    // Información comercial
    industry: '',
    rating: 3,

    // Condiciones de pago
    paymentCondition: [],
    paymentTerms: 30,
    paymentMethod: [],
    hasCredit: false,
    creditLimit: '',
    advancePercentage: '',
    bankAccounts: [],

    // Notas
    notes: '',
    tags: []
  });

  const [contacts, setContacts] = useState([]);
  const [newContact, setNewContact] = useState({
    firstName: '',
    lastName: '',
    position: '',
    department: '',
    email: '',
    phone: '',
    mobile: '',
    isPrimary: false
  });

  const [showContactForm, setShowContactForm] = useState(false);
  const [showContactsList, setShowContactsList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [editingContactIndex, setEditingContactIndex] = useState(null);
  const [originalContactData, setOriginalContactData] = useState(null);
  const [themeColor, setThemeColor] = useState('#c4fffc');

  useEffect(() => {
    const update = () => setThemeColor(localStorage.getItem('theme-primary-color') || '#c4fffc');
    update();
    const interval = setInterval(update, 500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (partner) {
      // Normalize to arrays (old data may be strings)
      let pc = partner.paymentCondition;
      if (typeof pc === 'string' && pc) pc = [pc];
      else if (!Array.isArray(pc)) pc = [];

      let pm = partner.paymentMethod;
      if (typeof pm === 'string' && pm) pm = [pm];
      else if (!Array.isArray(pm)) pm = [];

      // Normalize bank accounts: old single fields → array
      let ba = partner.bankAccounts;
      if (!Array.isArray(ba) || ba.length === 0) {
        if (partner.bankName) {
          ba = [{ bankName: partner.bankName, bankAccountType: partner.bankAccountType || '', bankAccountNumber: partner.bankAccountNumber || '' }];
        } else {
          ba = [];
        }
      }

      setFormData({
        ...partner,
        paymentCondition: pc,
        paymentMethod: pm,
        bankAccounts: ba,
        tags: partner.tags || []
      });
      setContacts(partner.contacts || []);
    }
  }, [partner]);

  useEffect(() => {
    if (openContactsDirectly) {
      setShowContactsList(true);
    }
  }, [openContactsDirectly]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.legalName) newErrors.legalName = 'Razón social es requerida';
    if (!formData.isClient && !formData.isSupplier) {
      newErrors.type = 'Debe ser cliente o proveedor (o ambos)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSaving(true);
    try {
      // Flatten bankAccounts[0] to old single fields for DB compat
      const ba = formData.bankAccounts || [];
      const firstBank = ba[0] || {};
      const dataToSave = {
        ...formData,
        creditLimit: formData.creditLimit === '' ? null : Number(formData.creditLimit) || null,
        advancePercentage: formData.advancePercentage === '' ? null : Number(formData.advancePercentage) || null,
        paymentTerms: formData.paymentTerms === '' ? 30 : Number(formData.paymentTerms) || 30,
        bankName: firstBank.bankName || '',
        bankAccountType: firstBank.bankAccountType || '',
        bankAccountNumber: firstBank.bankAccountNumber || '',
        bankAccounts: ba,
        contacts: contacts
      };

      if (partner) {
        await api.put(`/business-partners/${partner.id}`, dataToSave);
      } else {
        await api.post('/business-partners', dataToSave);
      }

      onClose();
    } catch (error) {
      console.error('Error saving partner:', error);
      const msg = error.response?.data?.message || 'Error al guardar. Por favor intente nuevamente.';
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleAddContact = async () => {
    if (!newContact.email) {
      alert('Por favor complete el email del contacto');
      return;
    }

    let updatedContacts;

    if (editingContactIndex !== null) {
      // Editando contacto existente
      updatedContacts = [...contacts];
      updatedContacts[editingContactIndex] = {
        ...newContact,
        isPrimary: contacts[editingContactIndex].isPrimary // Mantener el estado de principal
      };
      setEditingContactIndex(null);
    } else {
      // Agregando nuevo contacto
      const contactToAdd = {
        ...newContact,
        isPrimary: contacts.length === 0
      };
      updatedContacts = [...contacts, contactToAdd];
    }

    setContacts(updatedContacts);

    // Si está en modo de contactos directos, guardar automáticamente
    if (openContactsDirectly && partner) {
      try {
        const dataToSave = {
          ...partner,
          contacts: updatedContacts
        };
        await api.put(`/business-partners/${partner.id}`, dataToSave);
      } catch (error) {
        console.error('Error saving contact:', error);
        alert('Error al guardar el contacto');
        // Revertir el cambio si hay error
        setContacts(contacts);
        return;
      }
    }

    setNewContact({
      firstName: '',
      lastName: '',
      position: '',
      department: '',
      email: '',
      phone: '',
      mobile: '',
      isPrimary: false
    });
    setEditingContactIndex(null);
    setOriginalContactData(null);
    setShowContactForm(false);
  };

  const handleDeleteContact = async (index) => {
    const wasPrimary = contacts[index].isPrimary;
    const newContacts = contacts.filter((_, i) => i !== index);

    // Si eliminamos el contacto principal y quedan otros, hacer el primero principal
    if (wasPrimary && newContacts.length > 0) {
      newContacts[0].isPrimary = true;
    }

    setContacts(newContacts);

    // Si está en modo de contactos directos, guardar automáticamente
    if (openContactsDirectly && partner) {
      try {
        const dataToSave = {
          ...partner,
          contacts: newContacts
        };
        await api.put(`/business-partners/${partner.id}`, dataToSave);
      } catch (error) {
        console.error('Error deleting contact:', error);
        alert('Error al eliminar el contacto');
        // Revertir el cambio si hay error
        setContacts(contacts);
        return;
      }
    }
  };

  const handleSetPrimaryContact = async (index) => {
    const newContacts = contacts.map((contact, i) => ({
      ...contact,
      isPrimary: i === index
    }));
    setContacts(newContacts);

    // Si está en modo de contactos directos, guardar automáticamente
    if (openContactsDirectly && partner) {
      try {
        const dataToSave = {
          ...partner,
          contacts: newContacts
        };
        await api.put(`/business-partners/${partner.id}`, dataToSave);
      } catch (error) {
        console.error('Error updating primary contact:', error);
        alert('Error al actualizar el contacto principal');
        // Revertir el cambio si hay error
        setContacts(contacts);
        return;
      }
    }
  };

  const handleEditContact = (index) => {
    const contactToEdit = contacts[index];
    const contactData = {
      firstName: contactToEdit.firstName || '',
      lastName: contactToEdit.lastName || '',
      position: contactToEdit.position || '',
      department: contactToEdit.department || '',
      email: contactToEdit.email || '',
      phone: contactToEdit.phone || '',
      mobile: contactToEdit.mobile || '',
      isPrimary: contactToEdit.isPrimary || false
    };

    setNewContact(contactData);
    setOriginalContactData(contactData); // Guardar datos originales
    setEditingContactIndex(index);
    setShowContactForm(true);
  };

  // Función para verificar si hay cambios en el contacto
  const hasContactChanges = () => {
    if (!originalContactData || editingContactIndex === null) return true; // Para nuevo contacto, siempre habilitado

    return Object.keys(newContact).some(key => {
      return newContact[key] !== originalContactData[key];
    });
  };

  if (!isOpen) return null;

  return (
    <div className="business-modal-overlay">
      <div className="business-modal">
        {/* Header */}
        <div className="business-modal-header">
          <h2>
            {partner ? 'Editar' : 'Nuevo'} {type === 'client' ? 'Cliente' : 'Proveedor'}
          </h2>
          <button className="modal-close" onClick={onClose} type="button">
            ×
          </button>
        </div>

        {/* Content */}
        <div className="business-modal-content">
          {!showContactsList ? (
            <form onSubmit={handleSubmit} className="form-grid">
              {/* Información de la Empresa - ANCHO COMPLETO */}
              <div className="form-section full-width">
                <h3>Información de la Empresa</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label>Razón Social *</label>
                    <input
                      type="text"
                      value={formData.legalName}
                      onChange={(e) => setFormData({...formData, legalName: e.target.value})}
                      className={errors.legalName ? 'error' : ''}
                      placeholder="Empresa S.A."
                    />
                    {errors.legalName && <span className="error-message">{errors.legalName}</span>}
                  </div>
                  <div className="form-group">
                    <label>Nombre de Fantasía</label>
                    <input
                      type="text"
                      value={formData.tradeName}
                      onChange={(e) => setFormData({...formData, tradeName: e.target.value})}
                      placeholder="Nombre comercial"
                    />
                  </div>
                  <div className="form-group">
                    <label>RUT</label>
                    <input
                      type="text"
                      value={formData.taxId}
                      onChange={(e) => setFormData({...formData, taxId: e.target.value})}
                      placeholder="12.345.678-9"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Tipo de Relación</label>
                    <div className="type-selection">
                      <div className="type-checkbox">
                        <input
                          type="checkbox"
                          id="isClient"
                          checked={formData.isClient}
                          onChange={(e) => setFormData({...formData, isClient: e.target.checked})}
                        />
                        <label htmlFor="isClient">Es Cliente</label>
                      </div>
                      <div className="type-checkbox">
                        <input
                          type="checkbox"
                          id="isSupplier"
                          checked={formData.isSupplier}
                          onChange={(e) => setFormData({...formData, isSupplier: e.target.checked})}
                        />
                        <label htmlFor="isSupplier">Es Proveedor</label>
                      </div>
                    </div>
                    {errors.type && <span className="error-message">{errors.type}</span>}
                  </div>
                  <div className="form-group">
                    <label>Teléfono</label>
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+56 2 2345 6789"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="contacto@empresa.cl"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Dirección</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      placeholder="Av. Principal 123"
                    />
                  </div>
                  <div className="form-group">
                    <label>Ciudad</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      placeholder="Santiago"
                    />
                  </div>
                  <div className="form-group">
                    <label>País</label>
                    <input
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData({...formData, country: e.target.value})}
                      placeholder="Chile"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Sitio Web</label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({...formData, website: e.target.value})}
                      placeholder="https://www.empresa.cl"
                    />
                  </div>
                </div>
              </div>

              {/* Información Comercial */}
              <div className="form-section full-width">
                <h3>Información Comercial</h3>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Industria</label>
                      <input
                        type="text"
                        value={formData.industry}
                        onChange={(e) => setFormData({...formData, industry: e.target.value})}
                        placeholder="Tecnología, Minería, etc."
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Calificación</label>
                      <div className="rating-input">
                        {[1, 2, 3, 4, 5].map(star => (
                          <Star
                            key={star}
                            size={20}
                            className={star <= formData.rating ? 'filled' : ''}
                            onClick={() => setFormData({...formData, rating: star})}
                            style={{ cursor: 'pointer' }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Notas</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        rows={3}
                        placeholder="Observaciones adicionales..."
                      />
                    </div>
                  </div>
              </div>

              {/* Condiciones de Pago */}
              <div className="form-section full-width">
                <h3><CreditCard size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Condiciones de Pago</h3>

                {/* Condition pills - horizontal wrap */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif", display: 'block', marginBottom: '8px', color: '#374151' }}>Condiciones</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {[
                      { key: 'contado', label: 'Contado', icon: '💵' },
                      { key: 'credito', label: 'Crédito', icon: '📅' },
                      { key: 'anticipado', label: 'Anticipo', icon: '💰' },
                      { key: 'contra_entrega', label: 'Contra Entrega', icon: '📦' },
                      { key: 'por_avance', label: 'Por Avance', icon: '📊' }
                    ].map(c => {
                      const conditions = Array.isArray(formData.paymentCondition) ? formData.paymentCondition : [];
                      const active = conditions.includes(c.key);
                      return (
                        <button key={c.key} type="button" onClick={() => {
                          const updated = active ? conditions.filter(x => x !== c.key) : [...conditions, c.key];
                          setFormData({...formData, paymentCondition: updated});
                        }} style={{
                          padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                          fontFamily: "'Space Grotesk', sans-serif", cursor: 'pointer', transition: 'all 0.2s',
                          border: active ? '2px solid #000' : '2px solid #e5e7eb',
                          background: active ? themeColor : '#fff', color: '#000',
                          display: 'flex', alignItems: 'center', gap: '5px'
                        }}>
                          <span style={{ fontSize: '13px' }}>{c.icon}</span>
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Expandable details per active condition */}
                {(() => {
                  const conds = Array.isArray(formData.paymentCondition) ? formData.paymentCondition : [];
                  const showCredito = conds.includes('credito') || conds.includes('por_avance');
                  const showAnticipo = conds.includes('anticipado');
                  if (!showCredito && !showAnticipo) return null;
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                      {showCredito && (
                        <div style={{
                          background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '10px',
                          padding: '14px 16px'
                        }}>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: '#0369a1', marginBottom: '10px', fontFamily: "'Space Grotesk', sans-serif", display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Clock size={14} /> Detalles de Crédito
                          </div>
                          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 130px' }}>
                              <label style={{ fontSize: '11px', fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif", display: 'block', marginBottom: '4px', color: '#374151' }}>Plazo</label>
                              <select value={formData.paymentTerms || 30} onChange={(e) => setFormData({...formData, paymentTerms: parseInt(e.target.value)})} style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #bae6fd', fontSize: '13px', fontFamily: "'Space Grotesk', sans-serif", background: '#fff' }}>
                                <option value={30}>30 días</option>
                                <option value={60}>60 días</option>
                                <option value={90}>90 días</option>
                                <option value={120}>120 días</option>
                              </select>
                            </div>
                            <div style={{ flex: '1 1 160px' }}>
                              <label style={{ fontSize: '11px', fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif", display: 'block', marginBottom: '4px', color: '#374151' }}>Límite de Crédito (CLP)</label>
                              <input type="text"
                                value={formData.creditLimit ? Number(formData.creditLimit).toLocaleString('es-CL') : ''}
                                onChange={(e) => { const raw = e.target.value.replace(/[^\d]/g, ''); setFormData({...formData, creditLimit: raw ? parseInt(raw) : ''}); }}
                                placeholder="$0"
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #bae6fd', fontSize: '13px', fontFamily: "'Space Grotesk', sans-serif", boxSizing: 'border-box', background: '#fff' }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      {showAnticipo && (
                        <div style={{
                          background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '10px',
                          padding: '14px 16px'
                        }}>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: '#92400e', marginBottom: '10px', fontFamily: "'Space Grotesk', sans-serif", display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <DollarSign size={14} /> Detalles de Anticipo
                          </div>
                          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 130px' }}>
                              <label style={{ fontSize: '11px', fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif", display: 'block', marginBottom: '4px', color: '#374151' }}>Porcentaje (%)</label>
                              <input type="number" min="0" max="100" value={formData.advancePercentage || ''} onChange={(e) => setFormData({...formData, advancePercentage: e.target.value})} placeholder="Ej: 50"
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #fcd34d', fontSize: '13px', fontFamily: "'Space Grotesk', sans-serif", boxSizing: 'border-box', background: '#fff' }}
                              />
                            </div>
                            <div style={{ flex: '1 1 130px' }}>
                              <label style={{ fontSize: '11px', fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif", display: 'block', marginBottom: '4px', color: '#374151' }}>Pago saldo restante</label>
                              <select value={formData.paymentTerms || 30} onChange={(e) => setFormData({...formData, paymentTerms: parseInt(e.target.value)})} style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #fcd34d', fontSize: '13px', fontFamily: "'Space Grotesk', sans-serif", background: '#fff' }}>
                                <option value={0}>Contra entrega</option>
                                <option value={30}>30 días</option>
                                <option value={60}>60 días</option>
                                <option value={90}>90 días</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Payment methods - horizontal pills */}
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif", display: 'block', marginBottom: '8px', color: '#374151' }}>Formas de Pago</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {[
                      { key: 'transferencia', label: 'Transferencia' },
                      { key: 'cheque', label: 'Cheque' },
                      { key: 'letra', label: 'Letra de Cambio' },
                      { key: 'pagare', label: 'Pagaré' },
                      { key: 'factoring', label: 'Factoring' }
                    ].map(m => {
                      const methods = Array.isArray(formData.paymentMethod) ? formData.paymentMethod : [];
                      const checked = methods.includes(m.key);
                      return (
                        <button key={m.key} type="button" onClick={() => {
                          const updated = checked ? methods.filter(x => x !== m.key) : [...methods, m.key];
                          setFormData({...formData, paymentMethod: updated});
                        }} style={{
                          padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                          fontFamily: "'Space Grotesk', sans-serif", cursor: 'pointer', transition: 'all 0.2s',
                          border: checked ? '2px solid #000' : '2px solid #e5e7eb',
                          background: checked ? themeColor : '#fff', color: '#000'
                        }}>
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Datos Bancarios - sección separada, múltiples cuentas */}
              <div className="form-section full-width">
                <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span><Landmark size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Datos Bancarios</span>
                  <button type="button" onClick={() => {
                    const ba = [...(formData.bankAccounts || []), { bankName: '', bankAccountType: '', bankAccountNumber: '' }];
                    setFormData({...formData, bankAccounts: ba});
                  }} style={{
                    padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 700,
                    fontFamily: "'Space Grotesk', sans-serif", cursor: 'pointer',
                    border: '2px solid #000', background: themeColor, color: '#000',
                    display: 'flex', alignItems: 'center', gap: '4px'
                  }}>
                    <Plus size={13} /> Agregar Cuenta
                  </button>
                </h3>

                {(!formData.bankAccounts || formData.bankAccounts.length === 0) && (
                  <p style={{ fontSize: '12px', color: '#9ca3af', fontFamily: "'Space Grotesk', sans-serif", textAlign: 'center', padding: '12px 0' }}>
                    Sin cuentas bancarias registradas
                  </p>
                )}

                {(formData.bankAccounts || []).map((ba, idx) => (
                  <div key={idx} style={{
                    border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px',
                    marginBottom: '10px', background: '#fafafa', position: 'relative'
                  }}>
                    <button type="button" onClick={() => {
                      const updated = formData.bankAccounts.filter((_, i) => i !== idx);
                      setFormData({...formData, bankAccounts: updated});
                    }} style={{
                      position: 'absolute', top: '8px', right: '8px', background: 'none', border: 'none',
                      cursor: 'pointer', padding: '2px', color: '#ef4444'
                    }} title="Eliminar cuenta">
                      <Trash2 size={14} />
                    </button>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 140px', minWidth: '120px' }}>
                        <label style={{ fontSize: '11px', fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif", display: 'block', marginBottom: '4px' }}>Banco</label>
                        <select value={ba.bankName || ''} onChange={(e) => {
                          const updated = [...formData.bankAccounts]; updated[idx] = {...ba, bankName: e.target.value};
                          setFormData({...formData, bankAccounts: updated});
                        }} style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '12px', fontFamily: "'Space Grotesk', sans-serif" }}>
                          <option value="">Seleccionar</option>
                          <option value="Banco de Chile">Banco de Chile</option>
                          <option value="Banco Estado">Banco Estado</option>
                          <option value="Banco Santander">Banco Santander</option>
                          <option value="BCI">BCI</option>
                          <option value="Banco Itaú">Banco Itaú</option>
                          <option value="Scotiabank">Scotiabank</option>
                          <option value="Banco BICE">Banco BICE</option>
                          <option value="Banco Security">Banco Security</option>
                          <option value="Banco Falabella">Banco Falabella</option>
                          <option value="Banco Ripley">Banco Ripley</option>
                          <option value="Banco Consorcio">Banco Consorcio</option>
                          <option value="Otro">Otro</option>
                        </select>
                      </div>
                      <div style={{ flex: '1 1 120px', minWidth: '100px' }}>
                        <label style={{ fontSize: '11px', fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif", display: 'block', marginBottom: '4px' }}>Tipo</label>
                        <select value={ba.bankAccountType || ''} onChange={(e) => {
                          const updated = [...formData.bankAccounts]; updated[idx] = {...ba, bankAccountType: e.target.value};
                          setFormData({...formData, bankAccounts: updated});
                        }} style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '12px', fontFamily: "'Space Grotesk', sans-serif" }}>
                          <option value="">Seleccionar</option>
                          <option value="corriente">Corriente</option>
                          <option value="vista">Vista / RUT</option>
                          <option value="ahorro">Ahorro</option>
                        </select>
                      </div>
                      <div style={{ flex: '1 1 140px', minWidth: '120px' }}>
                        <label style={{ fontSize: '11px', fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif", display: 'block', marginBottom: '4px' }}>N° Cuenta</label>
                        <input type="text" value={ba.bankAccountNumber || ''} onChange={(e) => {
                          const updated = [...formData.bankAccounts]; updated[idx] = {...ba, bankAccountNumber: e.target.value};
                          setFormData({...formData, bankAccounts: updated});
                        }} placeholder="Número" style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '12px', fontFamily: "'Space Grotesk', sans-serif", boxSizing: 'border-box' }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Gestionar Contactos */}
              <div className="form-section full-width contacts-button-wrapper">
                  <button
                    type="button"
                    className="btn-contacts"
                    onClick={() => setShowContactsList(true)}
                  >
                    <div className="btn-contacts-content">
                      <div className="btn-contacts-icon">
                        <Users size={32} />
                      </div>
                      <div className="btn-contacts-text">
                        <h4>Gestionar Contactos</h4>
                        <p>{contacts.length} contacto{contacts.length !== 1 ? 's' : ''} agregado{contacts.length !== 1 ? 's' : ''}</p>
                      </div>
                      <ChevronRight size={24} />
                    </div>
                  </button>
              </div>
            </form>
          ) : (
            // Vista de Contactos
            <div className="contacts-view">
              <div className="contacts-view-header">
                <div className="contacts-header-left">
                  {!openContactsDirectly && (
                    <button
                      type="button"
                      className="btn-back"
                      onClick={() => setShowContactsList(false)}
                    >
                      ← Volver
                    </button>
                  )}
                  <h3>Gestión de Contactos</h3>
                </div>
              </div>

              <div className="contacts-content">
                {!showContactForm && (
                  <button
                    type="button"
                    className="btn-add-contact"
                    onClick={() => setShowContactForm(true)}
                  >
                    <Plus size={16} />
                    Agregar Nuevo Contacto
                  </button>
                )}

                {showContactForm && (
                  <div className="contact-form">
                    <h4>{editingContactIndex !== null ? 'Editar Contacto' : 'Nuevo Contacto'}</h4>
                    <div className="contact-form-grid">
                      <div className="form-group">
                        <label>Nombre</label>
                        <input
                          type="text"
                          value={newContact.firstName}
                          onChange={(e) => setNewContact({...newContact, firstName: e.target.value})}
                          placeholder="Juan"
                        />
                      </div>
                      <div className="form-group">
                        <label>Apellido</label>
                        <input
                          type="text"
                          value={newContact.lastName}
                          onChange={(e) => setNewContact({...newContact, lastName: e.target.value})}
                          placeholder="Pérez"
                        />
                      </div>
                      <div className="form-group">
                        <label>Cargo</label>
                        <input
                          type="text"
                          value={newContact.position}
                          onChange={(e) => setNewContact({...newContact, position: e.target.value})}
                          placeholder="Gerente de Compras"
                        />
                      </div>
                      <div className="form-group">
                        <label>Departamento</label>
                        <input
                          type="text"
                          value={newContact.department}
                          onChange={(e) => setNewContact({...newContact, department: e.target.value})}
                          placeholder="Adquisiciones"
                        />
                      </div>
                      <div className="form-group">
                        <label>Email *</label>
                        <input
                          type="email"
                          value={newContact.email}
                          onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                          placeholder="juan.perez@empresa.cl"
                        />
                      </div>
                      <div className="form-group">
                        <label>Teléfono</label>
                        <input
                          type="text"
                          value={newContact.phone}
                          onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                          placeholder="+56 2 2345 6789"
                        />
                      </div>
                    </div>

                    <div className="contact-form-actions">
                      <button
                        type="button"
                        className="btn-cancel-contact"
                        onClick={() => {
                          setShowContactForm(false);
                          setEditingContactIndex(null);
                          setOriginalContactData(null);
                          setNewContact({
                            firstName: '',
                            lastName: '',
                            position: '',
                            department: '',
                            email: '',
                            phone: '',
                            mobile: '',
                            isPrimary: false
                          });
                        }}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="btn-save-contact"
                        onClick={handleAddContact}
                        disabled={editingContactIndex !== null && !hasContactChanges()}
                      >
{editingContactIndex !== null ? 'Actualizar Contacto' : 'Guardar Contacto'}
                      </button>
                    </div>
                  </div>
                )}

                {!showContactForm && contacts.length > 0 ? (
                  <div className="contacts-list">
                    {contacts.map((contact, index) => (
                      <div key={index} className="contact-card">
                        <div className="contact-card-info">
                          <div className="contact-card-name">
                            {contact.firstName} {contact.lastName}
                            {contact.isPrimary && (
                              <span className="contact-primary-badge">Principal</span>
                            )}
                          </div>
                          <div className="contact-card-details">
                            {contact.position && `${contact.position}`}
                            {contact.department && ` - ${contact.department}`}
                          </div>
                          <div className="contact-card-contact">
                            {contact.email && (
                              <span><Mail size={14} /> {contact.email}</span>
                            )}
                            {contact.phone && (
                              <span><Phone size={14} /> {contact.phone}</span>
                            )}
                          </div>
                        </div>
                        <div className="contact-card-actions">
                          {!contact.isPrimary && (
                            <button
                              type="button"
                              className="btn-set-primary"
                              onClick={() => handleSetPrimaryContact(index)}
                              title="Establecer como principal"
                            >
                              <Star size={16} />
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn-edit-contact"
                            onClick={() => handleEditContact(index)}
                            title="Editar contacto"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            type="button"
                            className="btn-delete-contact"
                            onClick={() => handleDeleteContact(index)}
                            title="Eliminar contacto"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : !showContactForm ? (
                  <div className="no-contacts">
                    <Users size={48} />
                    <p>No hay contactos agregados</p>
                    <p className="text-muted">Agregue contactos para esta empresa</p>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!showContactsList && (
          <div className="business-modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={saving}
            >
              <Save size={18} />
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessPartnerModal;